import React, { useCallback, useEffect, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { BarChart3, RefreshCw } from 'lucide-react'
import { api, getApiErrorMessage } from '../lib/api'
import { Button } from '../components/ui/button'
import { Skeleton } from '../components/ui/skeleton'
import ClaimStatsPage from './ClaimStats'
import {
  OpsAcquisitionStatsPanel,
  OpsMembersStatsPanel,
  OpsServiceStatsPanel,
} from './OpsUsageStatsPanels'

const OPS_STATS_TABS = [
  { key: 'members', label: '회원·구독', path: '/ops/stats/members' },
  { key: 'service', label: '서비스 이용', path: '/ops/stats/service' },
  { key: 'acquisition', label: '유입·전환', path: '/ops/stats/acquisition' },
  { key: 'claims', label: '청구', path: '/ops/stats/claims' },
]

const PERIOD_OPTIONS = [
  { value: '1', label: '오늘' },
  { value: '7', label: '7일' },
  { value: '30', label: '30일' },
]

function unwrapData(response) {
  const payload = response?.data
  return payload?.data !== undefined ? payload.data : payload ?? null
}

function StatsSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((item) => <Skeleton key={item} className="h-36 rounded-md" />)}
      </div>
      <Skeleton className="h-80 rounded-md" />
    </div>
  )
}

export default function OpsStatsPage() {
  const { tab } = useParams()
  const activeTab = OPS_STATS_TABS.find((item) => item.key === tab)
  const [period, setPeriod] = useState('30')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    if (!activeTab || activeTab.key === 'claims') return
    setLoading(true)
    setError('')
    try {
      const response = await api.get('/ops/usage-dashboard/summary')
      setData(unwrapData(response))
    } catch (requestError) {
      setData(null)
      setError(getApiErrorMessage(requestError, '운영통계를 불러오지 못했습니다.'))
    } finally {
      setLoading(false)
    }
  }, [activeTab])

  useEffect(() => {
    load()
  }, [load])

  if (!activeTab) {
    return <Navigate to="/ops/stats/members" replace />
  }

  let content = null
  if (activeTab.key === 'claims') {
    content = <ClaimStatsPage embedded />
  } else if (loading && !data) {
    content = <StatsSkeleton />
  } else if (error) {
    content = (
      <div className="rounded-md border border-rose-200 bg-rose-50 p-5">
        <p className="font-semibold text-rose-800">{error}</p>
        <Button type="button" variant="outline" className="mt-4" onClick={load}>다시 불러오기</Button>
      </div>
    )
  } else if (activeTab.key === 'members') {
    content = <OpsMembersStatsPanel data={data} period={period} />
  } else if (activeTab.key === 'service') {
    content = <OpsServiceStatsPanel data={data} period={period} />
  } else if (activeTab.key === 'acquisition') {
    content = <OpsAcquisitionStatsPanel data={data} period={period} />
  }

  return (
    <section className="space-y-6 pb-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-100 text-slate-700">
              <BarChart3 className="h-5 w-5" aria-hidden="true" />
            </span>
            <h1 className="text-2xl font-bold text-slate-950">운영통계</h1>
          </div>
          <p className="mt-1 text-sm text-slate-500">회원 성장부터 서비스 이용, 유입 전환, 청구 처리까지 흐름별로 확인합니다.</p>
        </div>
        {activeTab.key !== 'claims' ? (
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md border border-slate-200 bg-white p-1" aria-label="집계 기간">
              {PERIOD_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={period === option.value}
                  onClick={() => setPeriod(option.value)}
                  className={`min-h-8 rounded px-3 text-sm font-semibold transition-colors ${
                    period === option.value ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <Button type="button" variant="outline" size="icon" onClick={load} disabled={loading} title="새로고침">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
              <span className="sr-only">새로고침</span>
            </Button>
          </div>
        ) : null}
      </header>

      <div className="border-b border-slate-200">
        <nav className="-mb-px flex min-w-0 gap-1 overflow-x-auto" aria-label="운영통계 탭">
          {OPS_STATS_TABS.map((item) => {
            const active = item.key === activeTab.key
            return (
              <Link
                key={item.key}
                to={item.path}
                aria-current={active ? 'page' : undefined}
                className={`inline-flex min-h-[2.75rem] shrink-0 items-center border-b-2 px-3 text-sm font-semibold transition-colors ${
                  active
                    ? 'border-slate-900 text-slate-950'
                    : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-800'
                }`}
              >
                {item.label}
              </Link>
            )
          })}
        </nav>
      </div>

      {content}
    </section>
  )
}